import type { LoginResult } from '../../services/http/api';

const STORAGE_KEY = 'asset.pending-context-switch.v1';
export const CONTEXT_SWITCH_PARAM = '_contextSwitch';
const MAX_AGE = 5 * 60_000;
type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export type BusinessSwitchTarget = { kind: 'book'; bookId: string; period?: string } | { kind: 'period'; bookId: string; period: string } | { kind: 'company'; tenantId: string; session: LoginResult } | { kind: 'session'; session: LoginResult; bookId: string | null; period?: string };
type Ticket = { version: 1; id: string; createdAt: number; source: string; destination: string; target: BusinessSwitchTarget; sessionBaseline?: string | null };

export function businessContextFingerprint(storage: Store) {
  const session = JSON.parse(storage.getItem('asset.session') || '{}') as LoginResult;
  const bookId = storage.getItem('asset.bookId');
  return JSON.stringify([session.user?.userId, session.tenant?.id, bookId, bookId ? storage.getItem(`asset.activePeriod.${bookId}`) : null, storage.getItem('asset.token')]);
}
function validateTarget(target: BusinessSwitchTarget, storage: Store) {
  const session = JSON.parse(storage.getItem('asset.session') || '{}') as LoginResult;
  if (!session.user?.userId || !session.tenant?.id) throw new Error('法人公司已切换，请重新操作');
  if (target.kind === 'session') {
    const next = target.session;
    if (next?.user?.userId !== session.user.userId || next?.tenant?.id !== session.tenant.id || !Array.isArray(next?.books)
      || (session.workspace?.id && next.workspace?.id !== session.workspace.id)) throw new Error('法人公司已切换，请重新操作');
    const books = next.books.filter((book) => !book.archivedAt);
    if (target.bookId === null ? books.length > 0 : !books.some((book) => book.id === target.bookId && /^\d{4}-(0[1-9]|1[0-2])$/.test(book.currentPeriod))) throw new Error('账套已切换，请重新操作');
    if (target.period !== undefined && (!target.bookId || !/^\d{4}-(0[1-9]|1[0-2])$/.test(target.period))) throw new Error('账套已切换，请重新操作');
  } else if (target.kind === 'company') {
    // Tenant membership can map the same person to a different tenant-local user.
    // The switch endpoint authorizes that mapping; the source fingerprint prevents
    // a late response from replacing a subsequently changed login/context.
    if (!target.session?.user?.userId || target.session?.tenant?.id !== target.tenantId || !target.session.token || !Array.isArray(target.session.books)
      || (session.workspace?.id && target.session.workspace?.id !== session.workspace.id)) throw new Error('法人公司已切换，请重新操作');
  } else {
    if (!session.books?.some((book) => book.id === target.bookId && !book.archivedAt)) throw new Error('账套已切换，请重新操作');
    if (target.kind === 'period' && (storage.getItem('asset.bookId') !== target.bookId || !/^\d{4}-(0[1-9]|1[0-2])$/.test(target.period))) throw new Error('账套已切换，请重新操作');
    if (target.kind === 'book' && target.period !== undefined && !/^\d{4}-(0[1-9]|1[0-2])$/.test(target.period)) throw new Error('账套已切换，请重新操作');
    if (!['period', 'book'].includes(target.kind)) throw new Error('账套已切换，请重新操作');
  }
}

/** Prepare a one-time, tab-local ticket. Never change live auth/book/period here. */
export function stageBusinessContextSwitch(target: BusinessSwitchTarget, source: string, io: { local: Store; pending: Store; url: string; id: string; now: number }) {
  if (!io.id || businessContextFingerprint(io.local) !== source) throw new Error('法人公司已切换，请重新操作');
  validateTarget(target, io.local);
  const url = new URL(io.url); url.searchParams.delete(CONTEXT_SWITCH_PARAM);
  const ticket: Ticket = { version: 1, id: io.id, createdAt: io.now, source, destination: url.href, target, ...(target.kind === 'session' ? { sessionBaseline: io.local.getItem('asset.session') } : {}) };
  io.pending.setItem(STORAGE_KEY, JSON.stringify(ticket));
  url.searchParams.set(CONTEXT_SWITCH_PARAM, ticket.id);
  return url.href;
}

/** Only the destination bootstrap may commit. Cancelling beforeunload leaves live context intact. */
export function applyBusinessContextSwitch(io: { local: Store; pending: Store; url: string; now: number; replaceUrl(url: string): void }) {
  const url = new URL(io.url), id = url.searchParams.get(CONTEXT_SWITCH_PARAM);
  if (!id) { try { io.pending.removeItem(STORAGE_KEY); } catch { /* No switch to commit; tab-storage cleanup is optional. */ } return false; }
  url.searchParams.delete(CONTEXT_SWITCH_PARAM); io.replaceUrl(url.href);
  let ticket: Ticket;
  try { ticket = JSON.parse(io.pending.getItem(STORAGE_KEY) || 'null'); } catch { io.pending.removeItem(STORAGE_KEY); return false; }
  if (!ticket || ticket.id !== id) return false;
  io.pending.removeItem(STORAGE_KEY);
  try {
    if (ticket.version !== 1 || ticket.destination !== url.href || !Number.isFinite(ticket.createdAt) || io.now < ticket.createdAt || io.now - ticket.createdAt > MAX_AGE || ticket.source !== businessContextFingerprint(io.local)) return false;
    if (ticket.target.kind === 'session' && ticket.sessionBaseline !== io.local.getItem('asset.session')) return false;
    validateTarget(ticket.target, io.local);
  } catch { return false; }
  const target = ticket.target;
  const changes: Array<[string, string | null]> = target.kind === 'session'
    ? [['asset.session', JSON.stringify(target.session)], ['asset.bookId', target.bookId], ...(target.bookId && target.period ? [[`asset.activePeriod.${target.bookId}`, target.period] as [string, string]] : [])]
    : target.kind === 'company'
    ? [['asset.token', target.session.token], ['asset.session', JSON.stringify(target.session)], ['asset.bookId', target.session.books.find((book) => !book.archivedAt)?.id || '']]
    : target.kind === 'book' ? [['asset.bookId', target.bookId], ...(target.period ? [[`asset.activePeriod.${target.bookId}`, target.period] as [string, string]] : [])] : [[`asset.activePeriod.${target.bookId}`, target.period]];
  const before = changes.map(([key]) => [key, io.local.getItem(key)] as const);
  try { for (const [key, value] of changes) { if (value === null) io.local.removeItem(key); else io.local.setItem(key, value); } }
  catch (error) { for (const [key, value] of before) { if (value === null) io.local.removeItem(key); else io.local.setItem(key, value); } throw error; }
  return true;
}

export type ContextQuery = { queryKey: readonly unknown[]; state: { data?: unknown } };
/** Audited in-memory drafts/receipts, including records whose page is no longer mounted. */
export function hasUnresolvedContextOperations(queries: ContextQuery[]) {
  return queries.some(({ queryKey, state: { data } }) => {
    if (!data || typeof data !== 'object') return false;
    const name = String(queryKey[0]);
    if (name === 'cashflow-draft') return Object.keys(data).length > 0;
    if (name === 'account-import-notice') return ['pending', 'unknown'].includes(String((data as { state?: string }).state));
    if (['system-config-draft', 'voucher-rule-draft', 'master-data-draft', 'bookset-uncertain', 'bookset-session-pending'].includes(name)) return true;
    if (name === 'bookset-draft') return Boolean((data as { dirty?: boolean }).dirty);
    return ['voucher-clone-operation', 'closing-operation'].includes(name)
      && ['pending', 'unknown', 'saved'].includes(String((data as { state?: string }).state));
  });
}

export const AUTOMATIC_RELOAD_EVENT = 'asset-before-automatic-reload';
/** Read live cache state at the event, avoiding subscription lag and stale closures. */
export function installBusinessUnloadGuard(target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>, queries: () => ContextQuery[], isWriting: () => boolean) {
  const warn = (event: Event) => {
    if (!hasUnresolvedContextOperations(queries())) return;
    event.preventDefault();
    // Native BeforeUnloadEvent uses a writable string; synthetic Event may expose only a boolean getter.
    if (typeof (event as BeforeUnloadEvent).returnValue === 'string') (event as BeforeUnloadEvent).returnValue = '';
  };
  const preventAutomaticReload = (event: Event) => {
    if (isWriting() || hasUnresolvedContextOperations(queries())) event.preventDefault();
  };
  target.addEventListener('beforeunload', warn);
  target.addEventListener(AUTOMATIC_RELOAD_EVENT, preventAutomaticReload);
  return () => { target.removeEventListener('beforeunload', warn); target.removeEventListener(AUTOMATIC_RELOAD_EVENT, preventAutomaticReload); };
}

/** Also honors mounted editors' existing beforeunload protection without opening a dialog. */
export function automaticReloadAllowed(target: Pick<EventTarget, 'dispatchEvent'>) {
  return target.dispatchEvent(new Event(AUTOMATIC_RELOAD_EVENT, { cancelable: true }))
    && target.dispatchEvent(new Event('beforeunload', { cancelable: true }));
}
